import BaseFooter from '@/presentation/components/ui/footer/base-footer'
import LoggedHeader from '@/presentation/components/ui/header/logged-header/logged-header'
import useAccount from '@/presentation/hooks/auth/use-account'
import { Box } from '@mui/material'
import { Suspense, useCallback, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import DesktopNavMenuMain from '../header/logged-header/desktop/desktop-nav-menu-main'
import Spinner from '../spinner/spinner'
import { DesktopNavMenuCompanyManager } from '../header/logged-header/desktop/desktop-nav-menu-company-manager'
import { DesktopNavMenuInternalCompanyManager } from '../header/logged-header/desktop/desktop-nav-menu-internal-company-manager'
import { RouteAnnouncer } from '../accessibility/route-announcer'
import { i18nTranslationHelper } from '@/i18n'

type NavMenuType = 'main' | 'companyManager' | 'internalCompanyManager'
type FooterVariant = 'full' | 'compact'

type LoggedLayoutProps = {
  navMenu?: NavMenuType
  footerVariant?: FooterVariant
  enableSearch?: boolean
}

const NAV_MENU_COMPONENTS = {
  main: DesktopNavMenuMain,
  companyManager: DesktopNavMenuCompanyManager,
  internalCompanyManager: DesktopNavMenuInternalCompanyManager
} as const

const LAYOUT_SX = {
  width: '100%',
  minHeight: '100vh',
  overflowX: 'hidden',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'white'
} as const

const MAIN_SX = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flexGrow: 1,
  bgcolor: 'white'
} as const

export default function LoggedLayout({
  navMenu = 'main',
  footerVariant = 'full',
  enableSearch = false
}: LoggedLayoutProps) {
  const t = i18nTranslationHelper.getTComponents
  const [account] = useAccount()
  const scrollRef = useRef<HTMLDivElement>()
  const [targetScroll, setTargetScroll] = useState<Node>()

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setTargetScroll(node)
      scrollRef.current = node
    }
  }, [])

  const getDesktopNavMenu = () => {
    if (navMenu === 'main' && !account?.passPlan) {
      return undefined
    }

    const NavComponent = NAV_MENU_COMPONENTS[navMenu]

    return NavComponent ? <NavComponent /> : undefined
  }

  return (
    <Box id="logged-layout" ref={setRefs} sx={LAYOUT_SX}>
      <RouteAnnouncer />

      <a href="#main-content" className="skip-link">
        {t('accessibility.skipLink')}
      </a>

      <LoggedHeader
        targetScroll={targetScroll}
        desktopNavMenu={getDesktopNavMenu()}
        enableSearch={enableSearch}
      />

      <Suspense fallback={<Spinner fullScreen />}>
        <Box component="main" role="main" id="main-content" sx={MAIN_SX}>
          <Outlet context={{ scrollRef }} />
        </Box>
      </Suspense>

      <BaseFooter variant={footerVariant} />
    </Box>
  )
}
